import React, { useCallback, useEffect, useRef } from "react";
import "./SingleCalendar.css";
import { Calendar } from "primereact/calendar";
import { getUtcDateWithLocalMidnight } from "../../utils/GeneralFunctions";
import { useTranslation } from "../../contexts/TranslationContext";
import {
  getPrimeReactDateFormat,
  getPrimeReactLocale,
  syncPrimeReactLocale,
} from "../../utils/primeReactLocale";

const SingleCalendar = ({
  placeholder,
  value,
  onChange,
  dateRef,
  min,
  max,
  onClearButtonClick,
  showTime = true,
  disabled = false,
  onHide,
  viewDate,
  ableToEdit = true,
}) => {
  const wrapperRef = useRef(null);
  const { currentLanguage } = useTranslation();
  const calendarLocale = getPrimeReactLocale(currentLanguage);
  const calendarDirection = "ltr";
  const calendarDateFormat = getPrimeReactDateFormat(currentLanguage);

  const normalizeCalendarDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return null;
    }

    return getUtcDateWithLocalMidnight(date);
  };

  useEffect(() => {
    syncPrimeReactLocale(currentLanguage);
  }, [currentLanguage]);

  const minDate = normalizeCalendarDate(min);
  const maxDate = normalizeCalendarDate(max);
  const normalizedViewDate = normalizeCalendarDate(viewDate);

  const setWrapperNode = useCallback(
    (node) => {
      wrapperRef.current = node;

      if (!dateRef) {
        return;
      }

      if (typeof dateRef === "function") {
        dateRef(node);
        return;
      }

      dateRef.current = node;
    },
    [dateRef]
  );

  const handleCalendarHide = useCallback(() => {
    const activeElement = document?.activeElement;

    if (
      wrapperRef.current?.contains(activeElement) &&
      typeof activeElement?.blur === "function"
    ) {
      activeElement.blur();
    }

    onHide?.();
  }, [onHide]);

  return (
    <div ref={setWrapperNode}>
      <Calendar
        locale={calendarLocale}
        dir={calendarDirection}
        minDate={minDate}
        maxDate={maxDate}
        showTime={showTime}
        value={value}
        onChange={onChange}
        className={`single-calendar ${!ableToEdit ? "readonly" : ""}`}
        placeholder={placeholder}
        disabled={disabled}
        showButtonBar
        onClearButtonClick={onClearButtonClick}
        onHide={handleCalendarHide}
        viewDate={normalizedViewDate}
        dateFormat={calendarDateFormat}
        hourFormat="24"
        appendTo="self"
      />
    </div>
  );
};

export default SingleCalendar;
